// File: client/src/components/rules/expressionEditor/SampleDataViewer.js
import React, { useState } from 'react';
import { useSampleData } from './SampleDataContext';
import { Card, Typography, Button, Space, Collapse, Tooltip, Badge } from 'antd';
import {
    LeftOutlined,
    RightOutlined,
    ReloadOutlined,
    PlayCircleOutlined,
    CaretRightOutlined,
    CopyOutlined,
    UpOutlined,
    DownOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { Panel } = Collapse;

const SampleDataViewer = ({
                              compact = false,
                              onInsertText = null, // Callback function for inserting text
                              onExecuteTest = null  // Callback function for testing with current sample
                          }) => {
    const {
        sampleRecords,
        currentSample,
        currentSampleIndex,
        nextSample,
        prevSample,
        refreshSamples,
        loading
    } = useSampleData();

    const [collapsed, setCollapsed] = useState(false);

    if (!currentSample) {
        return (
            <Card size="small">
                <Text type="secondary">No sample data available</Text>
            </Card>
        );
    }

    // Copy entire sample to clipboard
    const copyEntireSample = () => {
        const json = JSON.stringify(currentSample, null, 2);
        navigator.clipboard.writeText(json);
    };

    // Recursively render a nested object
    const renderNestedObject = (obj, level = 0, parentPath = '') => {
        if (!obj || typeof obj !== 'object') {
            return <Text type="secondary">null</Text>;
        }

        return (
            <div style={{ marginLeft: level > 0 ? 16 : 0 }}>
                {Object.entries(obj).map(([key, value]) => {
                    // Build the full path for this property
                    const fullPath = parentPath ? `${parentPath}.${key}` : `record.${key}`;

                    return (
                        <div key={key} style={{ marginBottom: 4 }}>
                            <Text
                                strong
                                style={{
                                    cursor: 'pointer',
                                    paddingRight: 4
                                }}
                                onClick={() => {
                                    if (onInsertText) {
                                        onInsertText(fullPath);
                                    }
                                }}
                            >
                                {key}:
                            </Text>
                            {renderValue(value, fullPath, level)}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Render a value based on its type
    const renderValue = (value, path, level) => {
        // Handle null or undefined
        if (value === null || value === undefined) {
            return <Text type="secondary">null</Text>;
        }

        // Handle nested objects
        if (typeof value === 'object' && !Array.isArray(value)) {
            return (
                <div>
                    <Text type="secondary">{'{'}</Text>
                    <div style={{ marginLeft: 16 }}>
                        {renderNestedObject(value, level + 1, path)}
                    </div>
                    <Text type="secondary">{'}'}</Text>
                </div>
            );
        }

        // Handle arrays
        if (Array.isArray(value)) {
            return (
                <div>
                    <Text type="secondary">[</Text>
                    <div style={{ marginLeft: 16 }}>
                        {value.map((item, index) => {
                            // Create proper array access syntax for the path
                            const itemPath = `${path}[${index}]`;

                            return (
                                <div key={index}>
                                    <Text
                                        type="secondary"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            if (onInsertText) {
                                                onInsertText(itemPath);
                                            }
                                        }}
                                    >
                                        {index}:
                                    </Text>{' '}
                                    {typeof item === 'object' && item !== null
                                        ? renderNestedObject(item, level + 1, itemPath)
                                        : (
                                            <Text
                                                style={{
                                                    cursor: 'pointer',
                                                    color: typeof item === 'string' ? '#a31515' :
                                                        typeof item === 'number' ? '#098658' :
                                                            typeof item === 'boolean' ? '#0000ff' : '#333'
                                                }}
                                                onClick={() => {
                                                    if (onInsertText) {
                                                        // Insert the literal value
                                                        const formattedValue = typeof item === 'string'
                                                            ? `"${item}"`
                                                            : String(item);
                                                        onInsertText(formattedValue);
                                                    }
                                                }}
                                            >
                                                {typeof item === 'string' ? `"${item}"` : String(item)}
                                            </Text>
                                        )
                                    }
                                </div>
                            );
                        })}
                    </div>
                    <Text type="secondary">]</Text>
                </div>
            );
        }

        // Handle primitive values
        return (
            <Text
                style={{
                    cursor: 'pointer',
                    color: typeof value === 'string' ? '#a31515' :
                        typeof value === 'number' ? '#098658' :
                            typeof value === 'boolean' ? '#0000ff' : '#333'
                }}
                onClick={() => {
                    if (onInsertText) {
                        const formattedValue = typeof value === 'string'
                            ? `"${value}"`
                            : String(value);
                        onInsertText(formattedValue);
                    }
                }}
            >
                {typeof value === 'string' ? `"${value}"` : String(value)}
            </Text>
        );
    };

    return (
        <Card
            size="small"
            title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <Text strong>Sample Data</Text>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Space size="small">
                            <Button
                                type="text"
                                size="small"
                                icon={<LeftOutlined />}
                                onClick={prevSample}
                                disabled={sampleRecords.length <= 1}
                            />

                            <Text code>{currentSample.id}</Text>
                            <Badge
                                count={`${currentSampleIndex + 1}/${sampleRecords.length}`}
                                style={{
                                    backgroundColor: '#f0f0f0',
                                    color: '#666',
                                    fontSize: '11px',
                                    fontWeight: 'normal',
                                    boxShadow: 'none'
                                }}
                            />

                            <Button
                                type="text"
                                size="small"
                                icon={<RightOutlined />}
                                onClick={nextSample}
                                disabled={sampleRecords.length <= 1}
                            />
                        </Space>
                    </div>

                    <Space>
                        <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={copyEntireSample}
                            title="Copy sample as JSON"
                        />
                        {onExecuteTest && (
                            <Button
                                type="text"
                                size="small"
                                icon={<PlayCircleOutlined />}
                                onClick={onExecuteTest}
                                title="Test with this sample"
                            />
                        )}
                        <Button
                            type="text"
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={refreshSamples}
                            loading={loading}
                            title="Refresh samples"
                        />
                        <Button
                            type="text"
                            size="small"
                            icon={collapsed ? <DownOutlined /> : <UpOutlined />}
                            onClick={() => setCollapsed(!collapsed)}
                            title={collapsed ? "Expand" : "Collapse"}
                        />
                    </Space>
                </div>
            }
            style={{ marginTop: 16 }}
        >
            {!collapsed && (
                <>
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                        <Collapse
                            bordered={false}
                            defaultActiveKey={['basic']}
                            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        >
                            <Panel header="Properties" key="basic">
                                {renderNestedObject(currentSample)}
                            </Panel>
                        </Collapse>
                    </div>

                    <div style={{ marginTop: 8, fontSize: 12, textAlign: 'center' }}>
                        <Text type="secondary">Click on property names or values to insert into expression</Text>
                    </div>
                </>
            )}
        </Card>
    );
};

export default SampleDataViewer;
